// Scratch of Fate (docs/04 §17.1). The foil is a grid of 3-px pixel blocks cleared by the brush.
// The view only reports reveal fractions; the sim decides voiding and payouts.
import { Container, Graphics, Rectangle, Sprite, type FederatedPointerEvent } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { audio } from '../../audio/audio';
import { formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { scratchValue } from '../../sim/bonus/scratch';
import { dispatch, getGame } from '../../state/store';
import { LOGICAL_W } from '../app';
import { PixelButton } from '../machine/PixelButton';
import { clock } from '../timeline/clock';
import type { Popups } from '../fx/Popups';

const CELL = 34;
const GAP = 4;
const BLOCK = 2;
const BLOCKS = CELL / BLOCK; // 17 × 17 blocks per cell
const BRUSH = 5; // in blocks
const REPORT_STEPS = [0.2, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

const PRIZE_SPRITE: Record<string, string> = {
  p2: 'prize_2',
  p5: 'prize_5',
  p10: 'prize_10',
  p25: 'prize_25',
  jackpot: 'prize_jackpot',
  eye: 'scratch_eye',
  dud: 'prize_dud',
  plug: 'item_plug_1',
};

export class ScratchView extends Container {
  private card = new Container();
  private foil: Graphics[] = [];
  private cleared: Uint8Array[] = [];
  private reported: number[] = [];
  private icons: Sprite[] = [];
  private cashout: PixelButton;
  private scratching = false;
  private lastSound = 0;
  private size = 3;

  constructor(private popups: Popups) {
    super();
    this.visible = false;
    this.addChild(this.card);
    this.cashout = new PixelButton(96, 15, t('ui.scratch.cashout', { coins: '0' }));
    this.cashout.onClick = () => dispatch({ t: 'BONUS_INPUT', input: { kind: 'scratch_cashout' } });
    this.card.eventMode = 'static';
    this.card.on('pointerdown', (e) => {
      this.scratching = true;
      audio.startLoop('scrape');
      this.scratch(e);
    });
    this.card.on('globalpointermove', (e) => this.scratching && this.scratch(e));
    const stop = () => {
      if (!this.scratching) return;
      this.scratching = false;
      audio.stopLoop('scrape');
    };
    this.card.on('pointerup', stop);
    this.card.on('pointerupoutside', stop);
  }

  open(): void {
    const b = getGame().run?.bonus;
    if (b?.mode !== 'scratch') return;
    this.size = b.size;
    this.card.removeChildren();
    this.foil = [];
    this.cleared = [];
    this.reported = [];
    this.icons = [];
    const n = this.size;
    const w = n * CELL + (n - 1) * GAP + 16;
    const h = w + 40;
    const frame = new Graphics();
    frame.rect(-2, -2, w + 4, h + 4).fill(PALETTE.ink);
    frame.rect(0, 0, w, h).fill(PALETTE.paper);
    frame.rect(0, 0, w, 14).fill(PALETTE.red);
    frame.rect(2, h - 3, w - 4, 1).fill(PALETTE.steel);
    const title = new Sprite(textTexture(t('ui.scratch.title'), 'white', 'blood'));
    title.position.set(Math.round((w - title.width) / 2), 3);
    this.card.addChild(frame, title);
    const a = getAtlas();
    b.cells.forEach((c, i) => {
      const x = 8 + (i % n) * (CELL + GAP);
      const y = 20 + Math.floor(i / n) * (CELL + GAP);
      const bg = new Graphics().rect(x, y, CELL, CELL).fill(PALETTE.cream);
      const icon = new Sprite(a.tex(PRIZE_SPRITE[c.prize] ?? 'prize_2'));
      icon.anchor.set(0.5);
      icon.position.set(x + CELL / 2, y + CELL / 2);
      const foil = new Graphics();
      foil.position.set(x, y);
      this.card.addChild(bg, icon, foil);
      this.icons.push(icon);
      this.foil.push(foil);
      const cl = new Uint8Array(BLOCKS * BLOCKS);
      // restore progress from state (e.g. after a reload)
      if (c.revealed >= 1) cl.fill(1);
      this.cleared.push(cl);
      this.reported.push(c.revealed);
      this.drawFoil(i);
    });
    this.card.hitArea = new Rectangle(0, 0, w, h);
    this.cashout.position.set(Math.round((w - 96) / 2), h - 19);
    this.card.addChild(this.cashout);
    this.card.position.set(Math.round(LOGICAL_W / 2 - w / 2 - 20), 360);
    this.visible = true;
    this.update();
    void clock.tween(this.card, 350, { y: 70, ease: 'back.out(1.4)' });
  }

  private drawFoil(i: number): void {
    const g = this.foil[i]!.clear();
    const cl = this.cleared[i]!;
    for (let by = 0; by < BLOCKS; by++)
      for (let bx = 0; bx < BLOCKS; bx++) {
        if (cl[by * BLOCKS + bx]) continue;
        const shade = (bx * 7 + by * 13) % 5 === 0 ? PALETTE.white : (bx + by) % 2 ? PALETTE.steel : PALETTE.paper;
        g.rect(bx * BLOCK, by * BLOCK, BLOCK, BLOCK).fill(shade);
      }
  }

  private scratch(e: FederatedPointerEvent): void {
    const b = getGame().run?.bonus;
    if (b?.mode !== 'scratch' || b.done) return;
    const p = this.card.toLocal(e.global);
    this.foil.forEach((f, i) => {
      const lx = (p.x - f.x) / BLOCK;
      const ly = (p.y - f.y) / BLOCK;
      if (lx < -BRUSH || ly < -BRUSH || lx > BLOCKS + BRUSH || ly > BLOCKS + BRUSH) return;
      const cl = this.cleared[i]!;
      let changed = false;
      for (let by = Math.floor(ly - BRUSH); by <= ly + BRUSH; by++)
        for (let bx = Math.floor(lx - BRUSH); bx <= lx + BRUSH; bx++) {
          if (bx < 0 || by < 0 || bx >= BLOCKS || by >= BLOCKS) continue;
          if ((bx - lx) ** 2 + (by - ly) ** 2 > BRUSH * BRUSH) continue;
          if (!cl[by * BLOCKS + bx]) {
            cl[by * BLOCKS + bx] = 1;
            changed = true;
          }
        }
      if (!changed) return;
      this.drawFoil(i);
      const frac = cl.reduce((a, v) => a + v, 0) / cl.length;
      const next = REPORT_STEPS.filter((s) => frac >= s && s > this.reported[i]!).pop();
      if (next !== undefined) {
        this.reported[i] = next;
        dispatch({ t: 'BONUS_INPUT', input: { kind: 'scratch', cell: i, revealed: frac } });
      }
      const now = performance.now();
      if (now - this.lastSound > 60) {
        this.lastSound = now;
        audio.play('scratch', { volume: 0.5 });
      }
    });
  }

  update(): void {
    const b = getGame().run?.bonus;
    if (b?.mode !== 'scratch') return;
    const v = scratchValue(b);
    this.cashout.setText(t('ui.scratch.cashout', { coins: formatNumber(v, getGame().meta.settings.notation) }));
  }

  async close(payout: string, voided: boolean): Promise<void> {
    const mid = { x: this.card.x + this.card.width / 2, y: this.card.y + this.card.height / 2 };
    if (voided) {
      audio.play('scratchVoid');
      this.foil.forEach((_, i) => this.cleared[i]!.fill(1));
      this.foil.forEach((_, i) => this.drawFoil(i));
      await this.popups.banner(t('ui.scratch.void'), mid.x, mid.y, 'red', 3, 700);
    } else if (payout !== '0') {
      audio.play('scratchWin');
      await this.popups.banner(t('ui.scratch.win', { coins: formatNumber(payout) }), mid.x, mid.y, 'gold', 2, 800);
    } else {
      await this.popups.banner(t('ui.scratch.nothing'), mid.x, mid.y, 'paper', 2, 500);
    }
    await clock.tween(this.card, 300, { y: 380, ease: 'back.in(1.4)' });
    this.visible = false;
  }
}
